import { FC, useState, SyntheticEvent, ReactNode } from "react";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardDialog } from "@/dashboard-design-system/Dialog";

export interface SaveEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCloseAndExecute: () => void;
  /** Should return true if save succeeded, false if it failed */
  onSave: () => Promise<boolean>;
  onDontSave: () => Promise<void>;
  title?: string;
  content?: ReactNode;
  isSaveDisabled?: boolean;
  isDontSaveDisabled?: boolean;
  dataTestPrefix?: string;
}

export const SaveEditorModal: FC<SaveEditorModalProps> = ({
  isOpen,
  onClose,
  onCloseAndExecute,
  onSave,
  onDontSave,
  title,
  content,
  isSaveDisabled = false,
  isDontSaveDisabled = false,
  dataTestPrefix = "save-editor-modal",
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const { t } = useTranslate();

  const handleDontSaveAndClose = async () => {
    await onDontSave();
    onCloseAndExecute();
  };

  const handleSave = async () => {
    setIsSaving(true);
    const success = await onSave();
    setIsSaving(false);

    if (success) {
      onCloseAndExecute();
    }
  };

  const dialogTitle = title ?? t("dashboard", "common.backToDashboard.confirmationModal.title");
  const dialogContent = content ?? t("dashboard", "common.backToDashboard.confirmationModal.content");

  return (
    <DashboardDialog
      maxWidth="xs"
      fullWidth
      open={isOpen}
      title={dialogTitle}
      actions={{
        main: {
          onClick: handleSave,
          children: t("dashboard", "common.backToDashboard.confirmationModal.save"),
          isLoading: isSaving,
          disabled: isSaveDisabled,
          "data-test": `${dataTestPrefix}-save-button`,
        },
        cancel: {
          children: t("dashboard", "common.backToDashboard.confirmationModal.dontSave"),
          onClick: handleDontSaveAndClose,
          disabled: isDontSaveDisabled,
        },
      }}
      onClose={onClose}
      PaperProps={{ onClick: (e: SyntheticEvent<unknown>) => e.stopPropagation() }}
      onClick={(e) => e.stopPropagation()}
      data-test={dataTestPrefix}
    >
      {dialogContent}
    </DashboardDialog>
  );
};
